import { createContext, useContext, useState } from 'react';
import { useGuides } from 'src/hook/components/useGuides';
import useUser from 'src/hook/data/useUser';
import { GetGuideAudioUrl } from 'src/utils/URLUtils';
import ChatInput from './ChatInput/ChatInput';
import ChatTitle from './ChatTitle/ChatTitle';
import ContextLessonsFirstVisit from './ContextLessonsFirstVisit/ContextLessonsFirstVisit';
import DiscoverFirstVisit from './DiscoverFirstVisit/DiscoverFirstVisit';
import styles from './GuideModalHost.module.scss';
import LessonsFirstVisit from './LessonsFirstVisit/LessonsFirstVisit';
import ScenarioChallenge from './ScenarioChallenge/ScenarioChallenge';

interface GuideAudioContextType {
    isPlaying: boolean;
    handleSpeakerClick: () => Promise<void>;
}

export const GuideAudioContext = createContext<GuideAudioContextType | null>(null);

export const useGuideAudio = () => {
    const context = useContext(GuideAudioContext);
    if (!context) {
        throw new Error('useGuideAudio must be used within a GuideAudioProvider');
    }
    return context;
};

export default function GuideModalHost() {
    const { visibleGuide, hideGuide } = useGuides();
    const { userInfo } = useUser();
    const [isPlaying, setIsPlaying] = useState(false);
    const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

    const handleSpeakerClick = async () => {
        try {
            if (isPlaying && audio) {
                await audio.pause();
                setIsPlaying(false);
            } else {
                if (!userInfo?.NativeLanguageCode || !visibleGuide) return;

                const newAudio = new Audio(GetGuideAudioUrl(`${userInfo.NativeLanguageCode}-${visibleGuide.toLowerCase()}`));

                // Add event listener for when audio ends
                newAudio.addEventListener('ended', () => {
                    setIsPlaying(false);
                });

                await newAudio.play();
                setAudio(newAudio);
                setIsPlaying(true);

                // Clean up previous audio if it exists
                if (audio) {
                    audio.pause();
                }
            }
        } catch (error) {
            console.error('Error playing audio:', error);
            setIsPlaying(false);
            setAudio(null);
        }
    };

    const renderGuide = () => {
        switch (visibleGuide) {
            case 'Discover-FirstVisit':
                return <DiscoverFirstVisit isPlaying={isPlaying} handleSpeakerClick={handleSpeakerClick} />;
            case 'Chat-Title':
                return <ChatTitle />;
            case 'Chat-Input':
                return <ChatInput isPlaying={isPlaying} handleSpeakerClick={handleSpeakerClick} />;
            case 'Lessons-FirstVisit':
                return <LessonsFirstVisit />;
            case 'ContextLessons-FirstVisit':
                return <ContextLessonsFirstVisit />;
            case 'scenario-challenge':
                return <ScenarioChallenge />;
            default:
                return null;
        }
    };

    if (!visibleGuide) return null;

    return (
        <GuideAudioContext.Provider value={{ isPlaying, handleSpeakerClick }}>
            <div onClick={() => hideGuide()} className={`${styles.overlay} ${styles.visible}`} />
            <div className={styles.visible}>
                {renderGuide()}
            </div>
        </GuideAudioContext.Provider>
    );
} 